import React from 'react';
import { ButtonProps } from '../../types';

const Button: React.FC<ButtonProps> = ({
  onClick,
  children,
  disabled = false,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  className = '',
}) => {
  // Base robotic classes
  const baseClasses = 'relative overflow-hidden font-display font-semibold tracking-wider uppercase transition-all duration-300 focus:outline-none group';
  
  // Variant classes with robotic styling
  const variantClasses = {
    primary: `
      bg-gradient-to-r from-primary-600 to-primary-700
      border border-primary-500 text-white
      shadow-neon hover:shadow-neon-lg hover:scale-105
      before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white before:to-transparent
      before:translate-x-[-100%] before:opacity-20 before:transition-transform before:duration-700
      hover:before:translate-x-[100%]
      focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-dark-950
    `,
    secondary: `
      bg-gradient-to-r from-secondary-600 to-secondary-700
      border border-secondary-500 text-white
      shadow-[0_0_5px_theme(colors.secondary.500)] hover:shadow-[0_0_20px_theme(colors.secondary.500)] hover:scale-105
      before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white before:to-transparent
      before:translate-x-[-100%] before:opacity-20 before:transition-transform before:duration-700
      hover:before:translate-x-[100%]
      focus:ring-2 focus:ring-secondary-400 focus:ring-offset-2 focus:ring-offset-dark-950
    `,
    success: `
      bg-gradient-to-r from-accent-600 to-accent-700
      border border-accent-500 text-white
      shadow-[0_0_5px_theme(colors.accent.500)] hover:shadow-[0_0_20px_theme(colors.accent.500)] hover:scale-105
      before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white before:to-transparent
      before:translate-x-[-100%] before:opacity-20 before:transition-transform before:duration-700
      hover:before:translate-x-[100%]
      focus:ring-2 focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-dark-950
    `,
    danger: `
      bg-gradient-to-r from-red-600 to-red-700
      border border-red-500 text-white
      shadow-[0_0_5px_theme(colors.red.500)] hover:shadow-[0_0_20px_theme(colors.red.500)] hover:scale-105
      before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white before:to-transparent
      before:translate-x-[-100%] before:opacity-20 before:transition-transform before:duration-700
      hover:before:translate-x-[100%]
      focus:ring-2 focus:ring-red-400 focus:ring-offset-2 focus:ring-offset-dark-950
    `,
  };
  
  // Size classes
  const sizeClasses = {
    small: 'py-2 px-4 text-xs rounded-md',
    medium: 'py-3 px-6 text-sm rounded-lg',
    large: 'py-4 px-8 text-base rounded-xl',
  };
  
  // Width classes
  const widthClasses = fullWidth ? 'w-full' : '';
  
  // Disabled classes
  const disabledClasses = disabled
    ? 'opacity-50 cursor-not-allowed hover:scale-100 hover:shadow-none before:hidden'
    : 'cursor-pointer';
  
  // Combine all classes
  const buttonClasses = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${widthClasses}
    ${disabledClasses}
    ${className}
  `.replace(/\s+/g, ' ').trim();
  
  return (
    <button
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      <span className="relative z-10 flex items-center justify-center gap-2">
        {children}
      </span>
      
      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-2 h-2 border-l-2 border-t-2 border-current opacity-60"></div>
      <div className="absolute top-0 right-0 w-2 h-2 border-r-2 border-t-2 border-current opacity-60"></div>
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l-2 border-b-2 border-current opacity-60"></div>
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r-2 border-b-2 border-current opacity-60"></div>
      
      {/* Scanning line effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-white to-transparent animate-scan"></div>
      </div>
    </button>
  );
};

export default Button;